import Input from "components/input"
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useUpdate } from "@refinedev/core";
import toast from "react-hot-toast";
import { Label } from "components/label";
import Button from "components/button";
import { FullScreenLoading } from "components/fullScreenLoading";
import { IConditionDetail, IEditConditionForm } from "interfaces";
import { TextEditor } from "components/textEditor";
import { useState } from "react";

export interface EditConditionFormProps {
  id: string;
  defaultConditionData: IConditionDetail;
}

const EditConditionSchema = z.object({
  title_vi: z.string().min(1, "This field is required"),
  title_en: z.string().min(1, "This field is required"),
  content_vi: z.string().min(1, "This field is required"),
  content_en: z.string().min(1, "This field is required"),
});

export const EditConditionForm = ({id, defaultConditionData}: EditConditionFormProps) => {

  const [contentLanguage, setContentLanguage] = useState("vi");

  const navigate = useNavigate();
  const { control, register, handleSubmit, formState: { errors } } = useForm<IEditConditionForm, HttpError, IEditConditionForm>({
    defaultValues: {
      title_vi: defaultConditionData.title_vi,
      title_en: defaultConditionData.title_en,
      content_vi: defaultConditionData.content_vi,
      content_en: defaultConditionData.content_en
    },
    resolver: zodResolver(EditConditionSchema)
  });

  const { mutate, isLoading: isLoading } = useUpdate();

  const onSubmit = (values: IEditConditionForm) => {
    const submitValues = {about_us: {
      ...values,
      content_vi: values.content_vi,
      content_en: values.content_en
    }};
    mutate({
      resource: "content/about-us",
      id: `${id}/update`,
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        ...submitValues
      },
    },
    {
      onSuccess: () => {
        toast.success("Edit successfully");
        navigate(`/other/about-actiup/${id}`);
      },
    });
  }
  
  return (
    <div className="w-full">
      <div>
        <button 
          className={`px-4 py-2 rounded-tl-md rounded-tr-md ${errors["title_vi"] || errors["content_vi"] ? "border-red-500" : "border-magnolia-600"} border-solid border-[1px] ${contentLanguage === "vi" ? "bg-gray-300" : "bg-white"}`} 
          onClick={() => setContentLanguage("vi")}
        >
          Vietnam
        </button>
        <button 
          className={`px-4 py-2 rounded-tl-md rounded-tr-md ${errors["title_en"] || errors["content_en"] ? "border-red-500" : "border-magnolia-600"} border-solid border-[1px] ${contentLanguage === "en" ? "bg-gray-300" : "bg-white"}`}
          onClick={() => setContentLanguage("en")}
        >
          English
        </button>
      </div>
      <form className="w-full flex flex-col gay-y-2 sm:gap-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="box-container">
          <div>
            <div className="sm:col-span-full grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6" style={{display: contentLanguage === "vi" ? "grid" : "none"}}>
              <div className="sm:col-span-3">
                <Label 
                  required
                  id="title_vi" 
                  name="Title (vi)"  
                />
                <Input 
                  id="title_vi"
                  type="text"
                  placeholder="Title (vi)"
                  error={errors["title_vi"]}
                  {...register("title_vi")}
                />
                {errors["title_vi"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.title_vi.message}
                    </span>
                  </p> 
                ): null}
              </div>
              <div className="sm:col-span-6">
                <Label 
                  required
                  id="content_vi" 
                  name="Content (vi)"
                />
                <Controller
                  control={control}
                  name="content_vi"
                  render={({ field: { onChange, value = "" } }) => (
                    <TextEditor
                      value={value}
                      onChange={onChange}
                      />
                  )}
                />
                {errors["content_vi"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.content_vi.message}
                    </span>
                  </p> 
                ): null}
              </div>
            </div>
            <div className="sm:col-span-full grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6 w-full" style={{display: contentLanguage === "en" ? "grid" : "none"}}>
              <div className="sm:col-span-3">
                <Label 
                  required
                  id="title_en" 
                  name="Title (en)"  
                />
                <Input 
                  id="title_vi"
                  type="text"
                  placeholder="Title (en)"
                  error={errors["title_en"]}
                  {...register("title_en")}
                />
                {errors["title_en"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.title_en.message}
                    </span>
                  </p> 
                ): null}
              </div>
              <div className="sm:col-span-6">
                <Label 
                  required
                  id="content_en" 
                  name="Content (en)"
                />
                <Controller
                  control={control}
                  name="content_en"
                  render={({ field: { onChange, value = "" } }) => (
                    <TextEditor
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
                {errors["content_en"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.content_en.message}
                    </span>
                  </p> 
                ): null}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]" style={{boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px"}}>
          <div className="flex items-center justify-end gap-x-4">
            <Link to={`/other/about-actiup/${id}`}>
              <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                CANCEL
              </Button>
            </Link>
            <Button className="w-20" type="submit">
              SAVE
            </Button>
          </div>
        </div>
      </form>
      {isLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
} 